// Tenant User Dashboard - Green Door CRM
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { fetchTenantDashboard, fetchCalendarEvents, fetchCrmEntities, fetchTenantUsers, fetchHorses } from '../../lib/queryFunctions';
import { useAuth } from '../../context/AuthContext';
import { activityAPI, saleOpportunityAPI, saleAPI } from '../../services/api';
import api from '../../services/api';
import SidebarLayout from '../../components/layout/SidebarLayout';
import HorseshoeIcon from '../../components/icons/HorseshoeIcon';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Building2,
  Contact,
  Plus,
  CalendarDays,
  CheckCircle2,
  Circle,
  Clock,
  PauseCircle,
  XCircle,
  Tag,
  ArrowRight,
  Search,
  Users,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { TaskDetailDialog, TaskDialog } from '../../components/crm/ActivityDialogs';
import { EventDetailDialog, EventDialog } from '../../components/crm/ActivityDialogs';
import Nudges from '../../components/dashboard/Nudges';

// Horseshoe Icon (Final version - Pure outline)
const HorseIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 4 L6 10 C6 14.4 8.7 18 12 18 C15.3 18 18 14.4 18 10 L18 4" />
  </svg>
);

// Task Status Visual Indicator Component - Using Lucide icons like deliverables
const TaskStatusIndicator = ({ status, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };
  
  const sizeClass = sizeClasses[size] || sizeClasses.md;
  
  const statusConfig = {
    not_started: { Icon: Circle, className: 'text-gray-400' },
    in_progress: { Icon: Clock, className: 'text-blue-500' },
    on_hold: { Icon: PauseCircle, className: 'text-yellow-500' },
    completed: { Icon: CheckCircle2, className: 'text-green-500' },
    cancelled: { Icon: XCircle, className: 'text-red-500' },
  };
  
  const config = statusConfig[status] || statusConfig.not_started;
  const Icon = config.Icon;
  
  return <Icon className={`${sizeClass} ${config.className}`} />;
};

// Task status icons (legacy - keeping for compatibility)
const statusIcons = {
  not_started: Circle,
  in_progress: Clock,
  on_hold: PauseCircle,
  completed: CheckCircle2,
  cancelled: XCircle,
};

const statusColors = {
  not_started: 'text-gray-400',
  in_progress: 'text-blue-500',
  on_hold: 'text-yellow-500',
  completed: 'text-green-500',
  cancelled: 'text-red-500',
};

const DashboardCard = ({ title, icon: Icon, onClick, onAdd, showSearch, onSearch }) => (
  <Card className="bg-primary text-primary-foreground relative h-32 hover:shadow-lg transition-shadow">
    <div className="absolute top-4 left-4">
      <Icon className="w-8 h-8 text-white" />
    </div>
    <div className="absolute bottom-4 left-4">
      <span className="text-lg font-medium tracking-wide">{title}</span>
    </div>
    <div className="absolute top-4 right-4 z-20">
      <Button 
        size="icon" 
        variant="ghost" 
        className="h-8 w-8 text-white hover:bg-white/20 rounded-full"
        onClick={(e) => {
          e.stopPropagation();
          onAdd();
        }}
      >
        <Plus className="w-5 h-5" />
      </Button>
    </div>
    {showSearch && (
      <div className="absolute bottom-4 right-4 z-20">
        <Button 
          size="icon" 
          variant="ghost" 
          className="h-8 w-8 text-white hover:bg-white/20 rounded-full"
          onClick={(e) => {
            e.stopPropagation();
            if (onSearch) onSearch();
          }}
        >
          <Search className="w-5 h-5" />
        </Button>
      </div>
    )}
    {/* Main click area */}
    <div 
      className="absolute inset-0 cursor-pointer z-10" 
      onClick={onClick}
    />
  </Card>
);

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, isViewOnly, hasFinancialAccess } = useAuth();
  const viewOnly = isViewOnly();
  const qc = useQueryClient();

  // ── Server state ──────────────────────────────────────────────────────────
  const { data: dashboardData, isLoading: loading } = useQuery({
    queryKey: QK.tenantDashboard(),
    queryFn: fetchTenantDashboard(),
    onError: () => toast.error('Failed to load dashboard'),
  });

  // Week events for the upcoming-events widget
  const weekStart = useMemo(() => {
    const today = new Date();
    const d = new Date(today);
    d.setDate(today.getDate() - today.getDay());
    return d.toISOString().split('T')[0];
  }, []);
  const weekEnd = useMemo(() => {
    const today = new Date();
    const d = new Date(today);
    d.setDate(today.getDate() - today.getDay() + 6);
    return d.toISOString().split('T')[0];
  }, []);

  const { data: weekEvents = [] } = useQuery({
    queryKey: QK.weekEvents(weekStart, weekEnd),
    queryFn: fetchCalendarEvents(weekStart, weekEnd),
  });

  // Supporting data — all served from cache on return visits
  const { data: usersRaw = [] } = useQuery({
    queryKey: QK.tenantUsers(),
    queryFn: fetchTenantUsers(),
  });
  const fullUsers = usersRaw;
  const usersMap = useMemo(() => Object.fromEntries(
    usersRaw.map(u => [u.id, u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email])
  ), [usersRaw]);
  const users = useMemo(() => usersRaw.map(u => ({ id: u.id, name: usersMap[u.id] })), [usersRaw, usersMap]);

  const { data: allEntities = [] } = useQuery({
    queryKey: QK.crmEntities(),
    queryFn: fetchCrmEntities(),
  });
  const entities    = useMemo(() => Object.fromEntries(allEntities.map(e => [e.id, e])), [allEntities]);
  const associations = allEntities;
  const allAccounts  = useMemo(() => allEntities.filter(e => e.crm_class === 'account'), [allEntities]);

  const { data: allHorsesData = [] } = useQuery({
    queryKey: QK.horses({ include_archived: true }),
    queryFn: fetchHorses({ include_archived: true }),
  });
  const allHorses = allHorsesData;
  const horsesMap = useMemo(() => Object.fromEntries(allHorsesData.map(h => [h.id, h.system_name || h.name || 'Unnamed'])), [allHorsesData]);

  // Horses map for task horse_id lookups (from dashboard tasks)
  const horses = useMemo(() => {
    const taskHorseIds = new Set((dashboardData?.my_tasks || []).map(t => t.horse_id).filter(Boolean));
    return Object.fromEntries(allHorsesData.filter(h => taskHorseIds.has(h.id)).map(h => [h.id, h.system_name || h.name]));
  }, [allHorsesData, dashboardData]);

  const invalidateDashboard = () => {
    qc.invalidateQueries({ queryKey: QK.tenantDashboard() });
    qc.invalidateQueries({ queryKey: QK.weekEvents(weekStart, weekEnd) });
  };

  // ── UI state ──────────────────────────────────────────────────────────────
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskDetailOpen, setTaskDetailOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetailOpen, setEventDetailOpen] = useState(false);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonationData, setImpersonationData] = useState(null);

  // Quick search state
  const [accountSearchOpen, setAccountSearchOpen] = useState(false);
  const [salesSearchOpen, setSalesSearchOpen] = useState(false);
  const [accountSearchQuery, setAccountSearchQuery] = useState('');
  const [salesSearchQuery, setSalesSearchQuery] = useState('');
  const [salesOpportunities, setSalesOpportunities] = useState([]);
  const [salesOppLoading, setSalesOppLoading] = useState(false);
  const [salesMap, setSalesMap] = useState({});

  // ── Impersonation check on mount ─────────────────────────────────────────
  useEffect(() => {
    const impersonationId = localStorage.getItem('impersonation_id');
    const originalUser = localStorage.getItem('original_user');
    
    if (impersonationId && originalUser) {
      setIsImpersonating(true);
      const originalUserData = JSON.parse(originalUser);
      setImpersonationData({
        impersonationId,
        originalUser: originalUserData,
        tenantName: user?.tenant_name || 'Unknown Tenant'
      });
    }
  }, [user]);

  const handleExitImpersonation = async () => {
    try {
      const impersonationId = localStorage.getItem('impersonation_id');
      const originalUser = localStorage.getItem('original_user');
      
      if (!impersonationId || !originalUser) {
        toast.error('Impersonation data not found');
        return;
      }

      // Call backend to end impersonation session using original system owner token
      const originalToken = localStorage.getItem('original_token') || localStorage.getItem('token');
      const response = await fetch(
        `${window.location.origin}/api/impersonation/end/${impersonationId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${originalToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to end impersonation');
      }

      // Restore original user and JWT
      const originalUserData = JSON.parse(originalUser);
      localStorage.setItem('user', JSON.stringify(originalUserData));
      localStorage.setItem('token', originalToken);
      
      // Clean up impersonation data
      localStorage.removeItem('impersonation_id');
      localStorage.removeItem('original_user');
      localStorage.removeItem('original_token');
      
      toast.success('Exited impersonation mode');
      
      // Force page reload to apply restored user and redirect to system dashboard
      window.location.href = '/admin/system';
      
    } catch (error) {
      console.error('Failed to exit impersonation:', error);
      toast.error('Failed to exit impersonation');
    }
  };

  // --- Quick Search Helpers ---

  // Returns the set of user IDs to filter by (own or dept for team leader)
  const getScopedUserIds = () => {
    const isTeamLeader = user?.roles?.includes('tenant_team_leader');
    if (!isTeamLeader) return new Set([user?.id]);
    const dept = user?.team_leader_department || user?.department;
    const deptIds = fullUsers
      .filter(u => u.department === dept || u.id === user?.id)
      .map(u => u.id);
    return new Set(deptIds.length ? deptIds : [user?.id]);
  };

  const getFilteredAccounts = (query) => {
    const scopedIds = getScopedUserIds();
    return allAccounts.filter(acc => {
      const inScope = scopedIds.has(acc.account_manager_id) || scopedIds.has(acc.account_admin_id);
      if (!inScope) return false;
      if (!query) return true;
      return (acc.system_name || acc.name || '').toLowerCase().includes(query.toLowerCase());
    });
  };

  const getFilteredOpportunities = (query) => {
    const scopedIds = getScopedUserIds();
    return salesOpportunities.filter(opp => {
      const inScope = scopedIds.has(opp.created_by_id);
      if (!inScope) return false;
      if (!query) return true;
      const horseName = horsesMap[opp.horse_id] || '';
      const saleName = salesMap[opp.sale_id] || '';
      const text = `${horseName} ${saleName}`.toLowerCase();
      return text.includes(query.toLowerCase());
    });
  };

  const openSalesSearch = async () => {
    setSalesSearchOpen(true);
    setSalesSearchQuery('');
    if (salesOpportunities.length === 0) {
      setSalesOppLoading(true);
      try {
        const res = await saleOpportunityAPI.list();
        setSalesOpportunities(res.data || []);
      } catch (e) {
        console.error('Failed to load opportunities:', e);
      } finally {
        setSalesOppLoading(false);
      }
    }
  };

  const openAccountSearch = () => {
    setAccountSearchOpen(true);
    setAccountSearchQuery('');
  };

  // --- End Quick Search Helpers ---

  const handleMarkComplete = async (taskId, e) => {
    e.stopPropagation();
    try {
      await activityAPI.update(taskId, { task_status: 'completed' });
      toast.success('Task marked as complete');
      invalidateDashboard();
    } catch (error) {
      console.error('Failed to mark task complete:', error);
      toast.error('Failed to update task');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <Skeleton className="h-32" />
             <Skeleton className="h-32" />
             <Skeleton className="h-32" />
             <Skeleton className="h-32" />
          </div>
          <Skeleton className="h-96" />
        </div>
      </SidebarLayout>
    );
  }

  const myTasks = (dashboardData?.my_tasks || []).slice().sort((a, b) => {
    const aStr = a.end_date_time ? String(a.end_date_time).split('T')[0] : 'z';
    const bStr = b.end_date_time ? String(b.end_date_time).split('T')[0] : 'z';
    return aStr.localeCompare(bStr);
  });

  return (
    <SidebarLayout>
      {/* Impersonation Banner */}
      {isImpersonating && impersonationData && (
        <div className="bg-orange-500 text-white px-6 py-3 mb-6 rounded-lg shadow-lg" data-testid="impersonation-banner">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5" />
              <div>
                <p className="font-semibold">
                  Impersonating: {user?.first_name} {user?.last_name} ({user?.email})
                </p>
                <p className="text-xs text-white/90">
                  Original User: {impersonationData.originalUser.email}
                </p>
              </div>
            </div>
            <Button
              onClick={handleExitImpersonation}
              variant="secondary"
              size="sm"
              className="bg-white text-orange-600 hover:bg-white/90"
            >
              Exit Impersonation
            </Button>
          </div>
        </div>
      )}
      
      <div className="space-y-8" data-testid="tenant-dashboard">
        {/* Greeting */}
        <div>
          <h1 className="font-serif text-3xl font-semibold text-foreground">
            Hello, {user?.first_name || 'there'}
          </h1>
          <p className="text-muted-foreground mt-1">
            Here is your Dashboard
          </p>
        </div>

        {/* Navigation Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <DashboardCard 
            title="ACCOUNTS" 
            icon={Building2} 
            onClick={() => navigate('/crm/accounts')}
            onAdd={() => navigate('/crm/accounts/new')}
            showSearch={true}
            onSearch={openAccountSearch}
          />
          <DashboardCard 
            title="SALE OPPORTUNITIES" 
            icon={Tag} 
            onClick={() => navigate('/sales/opportunities')}
            onAdd={() => navigate('/sales/opportunities/new')}
            showSearch={true}
            onSearch={openSalesSearch}
          />
          <DashboardCard 
            title="CONTACTS" 
            icon={Contact} 
            onClick={() => navigate('/crm/contacts')}
            onAdd={() => navigate('/crm/contacts/new')}
            showSearch={false}
          />
          <DashboardCard 
            title="HORSES" 
            icon={HorseIcon} 
            onClick={() => navigate('/horses')}
            onAdd={() => navigate('/horses/new')}
            showSearch={false}
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Nudges and My Tasks */}
          <div className="lg:col-span-2 space-y-6">
            {/* Nudges */}
            <Nudges />
            
            {/* My Tasks */}
            <Card data-testid="my-tasks-card">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div>
                  <CardTitle className="font-serif text-xl">My Tasks</CardTitle>
                  <CardDescription>Tasks assigned to you or created by you</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setTaskDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/tasks')}>
                    View All <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardHeader>
            <CardContent>
              <div className="overflow-y-auto max-h-[60vh] lg:max-h-[480px]" style={{ WebkitOverflowScrolling: 'touch' }}>
                {myTasks.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-20" />
                    <p>No pending tasks</p>
                  </div>
                ) : (
                  <div className="space-y-3 pr-4">
                    {myTasks.map((task) => {
                      // Check if task is overdue
                      const isOverdue = task.end_date_time && new Date(task.end_date_time) < new Date() && task.task_status !== 'completed' && task.task_status !== 'cancelled';

                      return (
                        <div
                          key={task.id}
                          onClick={() => { setSelectedTask(task); setTaskDetailOpen(true); }}
                          data-testid={`task-${task.id}`}
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 10px',
                            border: `1px solid ${isOverdue ? 'rgb(239 68 68)' : 'hsl(var(--border))'}`,
                            borderRadius: '6px',
                            cursor: 'pointer',
                            width: '100%',
                            boxSizing: 'border-box',
                          }}
                        >
                          {/* Checkmark */}
                          {task.task_status === 'completed' || task.task_status === 'cancelled' ? (
                            <div style={{ flexShrink: 0, width: '22px', height: '22px' }}>
                              <svg viewBox="0 0 24 24" fill="none" style={{ width: '22px', height: '22px', color: '#22c55e' }}>
                                <circle cx="12" cy="12" r="10" fill="currentColor" />
                                <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                          ) : (
                            <div
                              onClick={(e) => handleMarkComplete(task.id, e)}
                              style={{ flexShrink: 0, cursor: 'pointer', width: '22px', height: '22px' }}
                            >
                              <svg viewBox="0 0 24 24" fill="none" style={{ width: '22px', height: '22px', color: '#9ca3af' }}>
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                                <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.3" />
                              </svg>
                            </div>
                          )}
                          {/* Task content */}
                          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px' }}>
                              <p style={{ fontWeight: 500, fontSize: '14px', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.activity_name}</p>
                              <TaskStatusIndicator status={task.task_status} size="md" />
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2px 8px', fontSize: '12px', color: 'hsl(var(--muted-foreground))', marginTop: '2px' }}>
                              {(() => {
                                if (task.crm_entity_id || task.linked_account_id) {
                                  const entity = entities[(task.crm_entity_id || task.linked_account_id)];
                                  return entity ? (
                                    <span style={{ color: 'hsl(var(--primary))', display: 'flex', alignItems: 'center', gap: '2px' }}>
                                      {entity.crm_type === 'organization' ? <Building2 className="w-3 h-3" /> : <Contact className="w-3 h-3" />}
                                      {entity.system_name}
                                    </span>
                                  ) : null;
                                }
                                if (task.horse_id) {
                                  const horseName = horses[task.horse_id];
                                  return horseName ? (
                                    <span style={{ color: 'hsl(var(--primary))', display: 'flex', alignItems: 'center', gap: '2px' }}>
                                      <HorseshoeIcon className="w-3 h-3" />{horseName}
                                    </span>
                                  ) : null;
                                }
                                return null;
                              })()}
                              <span>
                                Due: {task.end_date_time ? new Date(task.end_date_time).toLocaleDateString() : 'None'}
                                {task.created_by_id === user?.id && task.assigned_to_user_id && task.assigned_to_user_id !== user?.id && usersMap[task.assigned_to_user_id] && (
                                  <span> · {usersMap[task.assigned_to_user_id]}</span>
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          </div>

          {/* Right Column - This Week Calendar */}
          <Card data-testid="calendar-preview-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div>
                <CardTitle className="font-serif text-xl">This Week</CardTitle>
                <p className="text-xs text-muted-foreground">
                  {(() => {
                    const today = new Date();
                    const weekLater = new Date();
                    weekLater.setDate(today.getDate() + 6);
                    return `${today.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekLater.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
                  })()}
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setEventDialogOpen(true)}>
                  <Plus className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate('/calendar')}>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-y-auto max-h-[60vh] lg:max-h-[480px]" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="space-y-1 pr-4">
                  {(() => {
                    // Generate 7 days starting from Sunday of current week
                    const days = [];
                    const today = new Date();
                    const currentDay = today.getDay(); // 0 = Sunday, 6 = Saturday
                    
                    // Get Sunday of this week
                    const sunday = new Date(today);
                    sunday.setDate(today.getDate() - currentDay);
                    
                    for (let i = 0; i < 7; i++) {
                      const date = new Date(sunday);
                      date.setDate(sunday.getDate() + i);
                      const dateStr = date.toISOString().split('T')[0];
                      const isToday = dateStr === today.toISOString().split('T')[0];
                      
                      // Get events for this day
                      const dayEvents = weekEvents.filter(event => {
                        const eventStart = event.start_date_time || event.event_date;
                        const eventEnd = event.end_date_time || eventStart;
                        
                        if (!eventStart) return false;
                        
                        // Parse dates without UTC conversion
                        const parseLocalDate = (d) => {
                          const s = String(d).split('T')[0];
                          const [y, m, day] = s.split('-').map(Number);
                          return new Date(y, m - 1, day);
                        };
                        
                        const currentDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                        const startDate = parseLocalDate(eventStart);
                        const endDate = parseLocalDate(eventEnd);
                        
                        // Check if current date falls within event date range
                        return currentDate >= startDate && currentDate <= endDate;
                      });
                      
                      days.push({
                        date: date,
                        dateStr: dateStr,
                        isToday: isToday,
                        events: dayEvents
                      });
                    }
                    
                    const getEventTypeColor = (eventType) => {
                      const colors = {
                        birthday: 'bg-blue-100 text-blue-800',
                        race: 'bg-purple-100 text-purple-800',
                        sale: 'bg-green-100 text-green-800',
                        deadline: 'bg-red-100 text-red-800',
                        reminder: 'bg-orange-100 text-orange-800',
                        other: 'bg-gray-100 text-gray-800',
                      };
                      return colors[eventType] || colors.other;
                    };
                    
                    return days.map((day, idx) => (
                      <div 
                        key={idx} 
                        className={`flex gap-3 py-3 border-b last:border-b-0 ${
                          day.isToday ? 'bg-primary/10' : ''
                        }`}
                      >
                        {/* Date column - always visible */}
                        <div className="w-18 shrink-0">
                          <p className={`text-sm font-semibold ${day.isToday ? 'text-primary' : ''}`}>
                            {day.date.toLocaleDateString('en-US', { weekday: 'short' })}
                          </p>
                          <p className={`text-xs ${day.isToday ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                            {day.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </p>
                        </div>
                        
                        {/* Events column */}
                        <div className="flex-1 space-y-1.5">
                          {day.events.length === 0 ? (
                            <p className="text-xs text-muted-foreground italic py-1.5">No events</p>
                          ) : (
                            day.events.map((event, eventIdx) => (
                              <div 
                                key={eventIdx}
                                className="p-2.5 border rounded-md hover:bg-muted/50 cursor-pointer transition-colors bg-white"
                                onClick={() => {
                                  setSelectedEvent(event);
                                  setEventDetailOpen(true);
                                }}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <p className="text-sm font-medium truncate flex-1">{event.activity_name}</p>
                                  <Badge className={`text-xs capitalize shrink-0 ${getEventTypeColor(event.event_type)}`}>
                                    {event.event_type?.replace('_', ' ') || 'Event'}
                                  </Badge>
                                </div>
                                {event.start_date_time && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {event.all_day ? 'All Day' : new Date(event.start_date_time).toLocaleTimeString('en-US', { 
                                      hour: 'numeric', 
                                      minute: '2-digit',
                                      hour12: true
                                    })}
                                  </p>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Task Detail Dialog */}
        <TaskDetailDialog
          open={taskDetailOpen}
          onOpenChange={setTaskDetailOpen}
          task={selectedTask}
          associations={associations}
          users={users}
          usersMap={usersMap}
          onSuccess={invalidateDashboard}
          onTaskUpdate={setSelectedTask}
        />
        
        {/* Event Detail Dialog */}

        
        {/* Task Creation Dialog */}
        <TaskDialog
          open={taskDialogOpen}
          onOpenChange={setTaskDialogOpen}
          accountId={null}
          horseId={null}
          onSuccess={() => {
            setTaskDialogOpen(false);
            invalidateDashboard();
          }}
          associations={[]}
          users={users}
          accounts={allAccounts}
          horses={allHorses}
          showAccountDropdown={true}
        />

        <EventDetailDialog
          open={eventDetailOpen}
          onOpenChange={setEventDetailOpen}
          event={selectedEvent}
          usersMap={usersMap}
          entities={entities}
          onNavigate={(path) => navigate(path)}
          onSuccess={() => {
            invalidateDashboard();
            
          }}
        />
        
        {/* Event Creation Dialog */}
        <EventDialog
          open={eventDialogOpen}
          onOpenChange={setEventDialogOpen}
          onSuccess={() => {
            invalidateDashboard();
            
          }}
        />

        {/* Account Quick Search Dialog */}
        <Dialog open={accountSearchOpen} onOpenChange={setAccountSearchOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                {user?.roles?.includes('tenant_team_leader') ? 'Department Accounts' : 'My Accounts'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                <Input
                  autoFocus
                  placeholder="Search accounts..."
                  value={accountSearchQuery}
                  onChange={(e) => setAccountSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <ScrollArea className="h-72">
                {(() => {
                  const filtered = getFilteredAccounts(accountSearchQuery);
                  if (filtered.length === 0) {
                    return (
                      <div className="text-center py-8 text-muted-foreground text-sm">
                        {accountSearchQuery ? 'No accounts match your search' : 'No accounts assigned to you'}
                      </div>
                    );
                  }
                  return (
                    <div className="space-y-1 pr-2">
                      {filtered.map(acc => {
                        const managerName = usersMap[acc.account_manager_id];
                        const adminName = usersMap[acc.account_admin_id];
                        return (
                          <div
                            key={acc.id}
                            className="flex items-center justify-between p-2.5 rounded-md hover:bg-muted/60 cursor-pointer transition-colors group"
                            onClick={() => {
                              setAccountSearchOpen(false);
                              navigate(`/crm/accounts/${acc.id}`);
                            }}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                                {acc.system_name || acc.name || 'Unnamed Account'}
                              </p>
                              {(managerName || adminName) && (
                                <p className="text-xs text-muted-foreground truncate">
                                  {managerName ? `Manager: ${managerName}` : `Admin: ${adminName}`}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 ml-2 shrink-0">
                              {acc.crm_class && (
                                <Badge variant="secondary" className="text-xs capitalize">
                                  {acc.crm_class}
                                </Badge>
                              )}
                              <ArrowRight className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </ScrollArea>
              <div className="pt-1 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs text-muted-foreground"
                  onClick={() => { setAccountSearchOpen(false); navigate('/crm/accounts'); }}
                >
                  View all accounts <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Sales Quick Search Dialog */}
        <Dialog open={salesSearchOpen} onOpenChange={setSalesSearchOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Tag className="w-5 h-5 text-primary" />
                {user?.roles?.includes('tenant_team_leader') ? 'Department Sale Opportunities' : 'My Sale Opportunities'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                <Input
                  autoFocus
                  placeholder="Search by horse or sale..."
                  value={salesSearchQuery}
                  onChange={(e) => setSalesSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <ScrollArea className="h-72">
                {salesOppLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  </div>
                ) : (() => {
                  const filtered = getFilteredOpportunities(salesSearchQuery);
                  if (filtered.length === 0) {
                    return (
                      <div className="text-center py-8 text-muted-foreground text-sm">
                        {salesSearchQuery ? 'No entries match your search' : 'No sale entries found'}
                      </div>
                    );
                  }
                  const stageColors = {
                    prospect: 'bg-gray-100 text-gray-700',
                    presale_confirm: 'bg-blue-100 text-blue-700',
                    active: 'bg-green-100 text-green-700',
                    sold: 'bg-emerald-100 text-emerald-700',
                    passed_in: 'bg-orange-100 text-orange-700',
                    withdrawn: 'bg-red-100 text-red-700',
                  };
                  return (
                    <div className="space-y-1 pr-2">
                      {filtered.map(opp => {
                        const creatorName = usersMap[opp.created_by_id];
                        const stageColor = stageColors[opp.stage] || 'bg-gray-100 text-gray-700';
                        const horseName = horsesMap[opp.horse_id] || 'Unnamed Horse';
                        const saleName = salesMap[opp.sale_id] || 'Unknown Sale';
                        return (
                          <div
                            key={opp.id}
                            className="flex items-center justify-between p-2.5 rounded-md hover:bg-muted/60 cursor-pointer transition-colors group"
                            onClick={() => {
                              setSalesSearchOpen(false);
                              navigate(`/sales/opportunities/${opp.id}`);
                            }}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                                {horseName}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {saleName}
                                {user?.roles?.includes('tenant_team_leader') && creatorName && (
                                  <span className="ml-1.5">· {creatorName}</span>
                                )}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 ml-2 shrink-0">
                              <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${stageColor}`}>
                                {(opp.stage || 'prospect').replace('_', ' ')}
                              </span>
                              <ArrowRight className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </ScrollArea>
              <div className="pt-1 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs text-muted-foreground"
                  onClick={() => { setSalesSearchOpen(false); navigate('/sales/opportunities'); }}
                >
                  View all opportunities <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </SidebarLayout>
  );
};

export default DashboardPage;
